// Video.js — Fully upgraded for React 18 + Editor.js v2.30+
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "./Video.css";
import videojs from "video.js";
import "video.js/dist/video-js.css";

const rewindSvg = `<svg width="18" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7 7.99067H1V1.99067M1 7.99067L5.64 3.63067C7.02091 2.25209 8.81245 1.35964 10.7447 1.08779C12.6769 0.815934 14.6451 1.17941 16.3528 2.12344C18.0605 3.06747 19.4152 4.54091 20.2126 6.32177C21.0101 8.10263 21.2072 10.0944 20.7742 11.997C20.3413 13.8996 19.3017 15.61 17.8121 16.8704C16.3226 18.1308 14.4637 18.873 12.5157 18.9851C10.5677 19.0973 8.63598 18.5732 7.01166 17.492C5.38734 16.4109 4.15839 14.831 3.51 12.9907" stroke="#101828" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;

const forwardSvg = `<svg width="18" height="20" viewBox="0 0 22 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M14.996 7.99806H20.996V1.99806M20.996 7.99806L16.366 3.63806C14.9866 2.2578 13.196 1.36325 11.2639 1.0892C9.3319 0.815161 7.36314 1.17648 5.65433 2.1187C3.94551 3.06092 2.58922 4.53301 1.78984 6.31313C0.990451 8.09325 0.791281 10.085 1.22234 11.9881C1.6534 13.8913 2.69133 15.6028 4.17973 16.8647C5.66813 18.1267 7.52636 18.8707 9.47439 18.9846C11.4224 19.0986 13.3547 18.5764 14.9801 17.4966C16.6055 16.4168 17.8359 14.838 18.486 12.9981" stroke="#101828" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;

if (!videojs.getPlugin("skipButtons")) {
  videojs.registerPlugin("skipButtons", function () {
    const player = this;

    const makeBtn = (label, icon, clickFn) => {
      const btn = videojs.dom.createEl("button", {
        className: "vjs-control vjs-button vjs-skip-button",
        title: label,
        innerHTML: `<span class="vjs-icon-placeholder" aria-hidden="true">${icon}</span>
                    <span class="vjs-control-text">${label}</span>`,
      });
      btn.addEventListener("click", clickFn);
      return btn;
    };

    player.ready(() => {
      const controlBar = player.getChild("ControlBar");
      const playToggleIndex = controlBar.children().findIndex(
        (c) => c.name?.() === "PlayToggle"
      );

      const backBtn = makeBtn("Rewind 5s", rewindSvg
, () => player.trigger("backward10"));
      const fwdBtn = makeBtn("Skip 5s", forwardSvg, () => player.trigger("forward10"));

      controlBar.el().insertBefore(backBtn, controlBar.children()[playToggleIndex]?.el());
      controlBar.el().insertBefore(fwdBtn, controlBar.children()[playToggleIndex + 1]?.el());
    });
  });
}

// -------------------- VideoPlayerGet Component --------------------
const VideoPlayerGet = ({ options, onReady }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    if (!options?.sources?.[0]?.src) return;

    if (!playerRef.current) {
      const videoElement = document.createElement("video-js");
      videoElement.classList.add("vjs-big-play-centered");
      videoRef.current.appendChild(videoElement);
      const player = videojs(videoElement, options, () => {
        videojs.log("player is ready");
        onReady && onReady(player);
      });
      playerRef.current = player;
    } else {
      const player = playerRef.current;
      player.autoplay(options.autoplay);
      player.src(options.sources);
    }
  }, [options, onReady]);

  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div data-vjs-player style={{ width: "100%", maxWidth: "960px" }}>
      <div ref={videoRef} />
    </div>
  );
};

// -------------------- VideoPlayer Component --------------------
const VideoPlayer = ({ url, libid }) => {
  const playerRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const playStartRef = useRef(null);
  const totalRef = useRef(
    parseFloat(localStorage.getItem(`watchedTime_${encodeURIComponent(url)}`)) || 0
  );
  const lastUpdateRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const loadVideo = async () => {
      setLoading(true);
      setError(null);
      try {
        const id = libid;
        const fileName = url
          .split("/")
          .pop()
          .replace(".mp4", "")
          .replaceAll(" ", "!")
          .replaceAll("&", "*")
          .replaceAll(",", "COMMA");
        const fileUrl = encodeURIComponent(fileName);

        const streamUrl = `https://d2rde65rx82s8d.cloudfront.net/tokengenerate?libid=${id}&id=${fileUrl}`;
        const res = await fetch(streamUrl, { signal: abortController.signal });
        const res1 = await res.json();
        if (!abortController.signal.aborted) {
          setVideoSrc(res1.playback_url);
          setLoading(false);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching tokenized stream:", err);
        if (!abortController.signal.aborted) {
          setError("Failed to load video stream");
          setLoading(false);
        }
      }
    };

    if (libid) {
      loadVideo();
    }


    return () => abortController.abort();
  }, [url, libid]);

  const videoJsOptions = useMemo(
    () => ({
      autoplay: false,
      controls: true,
      responsive: true,
      fluid: true,
      plugins: { skipButtons: {} },
      sources: videoSrc
        ? [{ src: videoSrc, type: "application/x-mpegURL" }]
        : [],
    }),
    [videoSrc]
  );

  const calcSegment = () => {
    if (playStartRef.current !== null) {
      const now = Date.now();
      const diff = (now - lastUpdateRef.current) / 1000;
      if (diff > 0) {
        totalRef.current += diff;
        localStorage.setItem(
          `watchedTime_${encodeURIComponent(url)}`,
          totalRef.current.toFixed(2)
        );
      }
      playStartRef.current = null;
      lastUpdateRef.current = now;
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") calcSegment();
    };
    const handleBeforeUnload = () => calcSegment();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const handlePlayerReady = (player) => {
  playerRef.current = player;

  player.on("play", () => {
    playStartRef.current = player.currentTime();
    lastUpdateRef.current = Date.now();
  });

  player.on("pause", calcSegment);
  player.on("ended", calcSegment);
  player.on("waiting", calcSegment);

  player.on("backward10", () => {
    player.currentTime(Math.max(0, player.currentTime() - 5));
  });

  player.on("forward10", () => {
    player.currentTime(
      Math.min(player.duration(), player.currentTime() + 5)
    );
  });

  player.on("playing", () => {
    if (playStartRef.current === null) {
      playStartRef.current = player.currentTime();
      lastUpdateRef.current = Date.now();
    }
  });

  // -------------------------------
  // Keyboard Shortcuts
  // -------------------------------

  const playerElement = player.el();

  // Make player focusable
  playerElement.setAttribute("tabindex", "0");

  const handleKeyDown = (e) => {
    const activeElement = document.activeElement;

    const isTyping =
      activeElement?.tagName === "INPUT" ||
      activeElement?.tagName === "TEXTAREA" ||
      activeElement?.isContentEditable;

    if (isTyping) return;

    switch (e.code) {
      case "ArrowLeft":
        e.preventDefault();
        player.trigger("backward10");
        break;

      case "ArrowRight":
        e.preventDefault();
        player.trigger("forward10");
        break;

      case "Space":
        e.preventDefault();

        if (player.paused()) {
          player.play();
        } else {
          player.pause();
        }
        break;

      default:
        break;
    }
  };

  // Attach keyboard listener to player
  playerElement.addEventListener("keydown", handleKeyDown);

  // Auto-focus when entering fullscreen
  player.on("fullscreenchange", () => {
    if (player.isFullscreen()) {
      setTimeout(() => {
        playerElement.focus();
      }, 100);
    }
  });

  player.on("dispose", () => {
    playerElement.removeEventListener(
      "keydown",
      handleKeyDown
    );

    videojs.log("player will dispose");
  });
};

  if (loading) return <div className="video-loading">Loading video...</div>;
  if (error) return <div className="video-error">⚠ {error}</div>;

  return (
    <div className="video-player-container">
      <VideoPlayerGet options={videoJsOptions} onReady={handlePlayerReady} />
    </div>
  );
};

// -------------------- InstantVideoTool (Editor.js Block) --------------------
export default class InstantVideoTool {
  constructor({ data, config, api, block }) {
    this.api = api;
    this.config = config;
    this.blockId = block?.id; 
    this.data = {
      url: data?.url || "",
      file: data?.file || null,
    };
    this.nodes = { holder: null };
    this._objectUrls = [];
    this._root = null; // React root
  }

  static get toolbox() {
    return {
      title: "Video",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
        <path d="M1.88477 11.1637C1.53667 11.1637 1.20283 11.0254 0.956689 10.7793C0.710547 10.5331 0.572266 10.1993 0.572266 9.85118V2.15119C0.572466 1.92429 0.631485 1.70132 0.743561 1.50403C0.855638 1.30675 1.01694 1.14189 1.21174 1.02554C1.40654 0.909188 1.62817 0.845326 1.85501 0.840181C2.08185 0.835037 2.30615 0.888786 2.50602 0.996184L9.68539 4.84619C9.78651 4.90077 9.87589 4.97473 9.94842 5.06385C10.021 5.15297 10.0752 5.25551 10.1081 5.3656C10.141 5.4757 10.1519 5.5912 10.1402 5.70551C10.1285 5.81981 10.0943 5.93069 10.0398 6.03181C9.98519 6.13293 9.91122 6.2223 9.8221 6.29484C9.73298 6.36737 9.63044 6.42164 9.52035 6.45455C9.41025 6.48745 9.29475 6.49835 9.18044 6.48662C9.06614 6.4749 8.95526 6.44077 8.85414 6.38619L2.32227 2.88618V9.12056L6.25977 7.00743C6.36131 6.9464 6.47419 6.90663 6.59156 6.89052C6.70894 6.87441 6.82836 6.88231 6.94259 6.91373C7.05682 6.94515 7.16348 6.99944 7.2561 7.07331C7.34873 7.14718 7.42538 7.23909 7.48143 7.34347C7.53747 7.44785 7.57174 7.56253 7.58214 7.68054C7.59255 7.79856 7.57888 7.91746 7.54197 8.03003C7.50505 8.14261 7.44566 8.24651 7.36739 8.33545C7.28912 8.42439 7.19361 8.49651 7.08664 8.54743L2.50602 11.0062C2.31513 11.1092 2.10168 11.1633 1.88477 11.1637Z" fill="#373739" fill-opacity="0.7"/>
      </svg>`,
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  renderEmptyState() {
    const container = document.createElement("div");
    container.classList.add("instant-video-tool-empty");

    const label = document.createElement("label");
    label.classList.add("video-upload-label");
    label.innerHTML = `
      <div style="display:flex;gap:8px; align-items: center">
      <div class="dragAndDropUploadBtn"><button class="buttonSecondaryGreysm undefined" style="display: flex; align-items: center; gap: 8px;"><span class="buttonLeadingIcon"><svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 12.5V15.8333C18 16.2754 17.8244 16.6993 17.5118 17.0118C17.1993 17.3244 16.7754 17.5 16.3333 17.5H4.66667C4.22464 17.5 3.80072 17.3244 3.48816 17.0118C3.17559 16.6993 3 16.2754 3 15.8333V12.5M14.6667 6.66667L10.5 2.5M10.5 2.5L6.33333 6.66667M10.5 2.5V12.5" stroke="#344054" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>Upload</button></div>
      <span>(or) drag and drop your video</span>
      </div>
    `;

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "video/*";
    fileInput.style.display = "none";

    fileInput.addEventListener("change", (e) => {
      const file = fileInput.files[0];
      if (file) this.handleVideoFile(file);
    });

    label.addEventListener("click", () => fileInput.click());
    label.addEventListener("dragover", (e) => {
      e.preventDefault();
      label.classList.add("dragover");
    });
    label.addEventListener("dragleave", () => label.classList.remove("dragover"));
    label.addEventListener("drop", (e) => {
      e.preventDefault();
      label.classList.remove("dragover");
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith("video/")) {
        this.handleVideoFile(file);
      }
    });

    container.appendChild(label);
    container.appendChild(fileInput);
    return container;
  }

  handleVideoFile(file) {
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    this._objectUrls.push(objectUrl);

    this.data.file = file;
    this.data.url = `library/content/${Date.now()}_${file.name}`;

    // this.syncBlockWithData();
    this.displayPreview(objectUrl);
  }

  displayPreview(videoUrl) {
    this.wrapper.innerHTML = "";
    const videoElement = document.createElement("video");
    videoElement.src = videoUrl;
    videoElement.controls = true;
    videoElement.style.width = "100%";
    videoElement.style.maxHeight = "400px";
    videoElement.classList.add("uploaded-video-preview");

    const notice = document.createElement("div");
    notice.classList.add("video-preview-notice");
    notice.textContent = "⏳ Video is being processed. Final version will appear after save.";

    this.wrapper.appendChild(videoElement);
    // this.wrapper.appendChild(notice);
  }

  syncBlockWithData() {
    if (this.blockId) {
      this.api.blocks.update(this.blockId, this.data);
    } else {
      console.warn("Block ID not available; update skipped.");
    }
  }

  render() {
    this.wrapper = document.createElement("div");
    this.wrapper.classList.add("instant-video-tool");

    if (!this.data.url && !this.data.file) {
      const emptyState = this.renderEmptyState();
      this.wrapper.appendChild(emptyState);
    } else if (this.data.file instanceof File) {
      console.log("2nd condition");
      const objectUrl = URL.createObjectURL(this.data.file);
      this._objectUrls.push(objectUrl);
      this.displayPreview(objectUrl);
    } else if (this.data.url) {
      if (this._root) this._root.unmount();
      this._root = createRoot(this.wrapper);
      this._root.render(<VideoPlayer url={this.data.url} libid={this.config.qnid} />);
    }

    this.nodes.holder = this.wrapper;
    return this.nodes.holder;
  }

  save() {
    return {
      url: this.data.url,
      file: this.data.file,
    };
  }

  destroy() {
    this._objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this._objectUrls = [];
    if (this._root) {
      this._root.unmount();
      this._root = null;
    }
    console.log("InstantVideoTool destroyed, resources cleaned up");
  }
}